import { DataSource } from '@angular/cdk/collections';
import { formatCurrency,getCurrencySymbol } from '@angular/common';
import { inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { Store } from '@ngrx/store';
import { BehaviorSubject,combineLatest,firstValueFrom,from,merge,Observable,of,Subscription,switchMap } from 'rxjs';
import { debounceTime,distinctUntilChanged,map,shareReplay,startWith,withLatestFrom } from 'rxjs/operators';
import {
AccountsReceivableLedgerEntity,
AccountsReceivableLedgerTypeEnum,ARLTypeEnumNames,
financialRound
} from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { CustomerEntity } from '../../../dave-data-module/entities/customer.entity';
import { LedgerImportDocumentTypes,LedgerImportEntity,LedgerImportStatusEnum } from '../../../dave-data-module/entities/ledger-import.entity';
import { MaterialToSupplierEntity } from '../../../dave-data-module/entities/materialToSupplier.entity';
import { ArlDataService } from '../../../dave-data-module/services/arl-data.service';
import { MaterialDataService } from '../../../dave-data-module/services/material-data.service';
import { MaterialToSupplierDataService } from '../../../dave-data-module/services/material-to-supplier-data.service';
import { State } from '../../../dave-data-module/State';
import { getQuantityTypeDictionary } from '../../../dave-data-module/State/selectors/accounting.selector';
import { getCustomerDictionary } from '../../../dave-data-module/State/selectors/customers.selectors';
import {
    getLedgerImportByCommissionId,
    getLedgerImports,
} from '../../../dave-data-module/State/selectors/ledger-import.selector';
import { isNotNullOrUndefined, uniqArray } from '../../../helper/helper';
import { _getArlChildren } from '../arl-calculation/arl-calculation.helper';
import { FilterTypes } from '../../../services/default-filter.service';
import { ArlFilterValues, SelectArlDialogItem } from '../select-arl-dialog/select-arl-dialog-data-source';

// export type NewOrderFromLedgerimportItem = Pick<AccountsReceivableLedgerEntity, 'MaterialId' | 'Type' | 'Quantity' | 'QuantityTypeId' | 'BookingText' | 'Information'>;
export type NewOrderFromLedgerimportItem = {
    ArlId: number;
    Type: string;
    Quantity: number;
    QuantityTypeId: number;
    quantityType: string;
    bookingText$: Observable<string>;
    Information: string;
    CostAmount: number;
    Tax: number;
    // SupplierId: number;

    costAmount: string;
    costAmountFromMaterialToSupplierOrMaterialAsNumber$: Observable<number>;
    costAmountFromMaterialToSupplierOrMaterial$: Observable<string>;
    alreadyOrderedQuantityForCommissionNew$: Observable<number | null>;
    alreadyOrderedQuantityForCommissionShipped$: Observable<number | null>;
    alreadyOrderedQuantityForCommissionBooked$: Observable<number | null>;
    materialId?: number;
    supplierName$: Observable<string>;
    /**
     * materialId|supplierId
     */
    id: string;
    orderedDiff$: Observable<number>;
    quantityForm: FormControl<number>;
    supplierForm: FormControl<CustomerEntity>;
};

type orderMaterialArls = { order: LedgerImportEntity; arls: AccountsReceivableLedgerEntity[] }[];
export class NewOrderFromLedgerimportDataSource extends DataSource<NewOrderFromLedgerimportItem> {
    paginator: MatPaginator;
    sort: MatSort | undefined;
    length$: Observable<number>;
    data$: Observable<NewOrderFromLedgerimportItem[]>;
    filteredData$: Observable<NewOrderFromLedgerimportItem[]>;
    isLoadingResults$ = new BehaviorSubject(false);
    materialToSupplierDataService$ = inject(MaterialToSupplierDataService);
    materialDataService$ = inject(MaterialDataService);
    filteredDataLength$ = new BehaviorSubject<number>(0);
    quantityFormCache = new Map<string, FormControl<number>>();
    supplierFormCache = new Map<string, FormControl<CustomerEntity>>();
    resetFormSubscription: Subscription | undefined;
    constructor(
        arls$: Observable<Pick<AccountsReceivableLedgerEntity, 'Id' |'MaterialId' | 'Type' | 'Quantity' | 'QuantityTypeId' | 'BookingText' | 'Information' | 'CostAmount' | 'Tax' | 'SupplierId'>[]>,
        supplierId$: Observable<number | null>,
        private commissionId$: Observable<number | null>,
        store: Store<State>,
        activeOrderStates: LedgerImportStatusEnum[],
        arlDataService: ArlDataService,
        documentType: LedgerImportDocumentTypes
    ) {
        super();
        const commissionArls$ = this.commissionId$.pipe(
            switchMap((commissionId) => {
                if (commissionId == null) {
                    return of({} as Record<number, AccountsReceivableLedgerEntity>);
                }
                return from(arlDataService.getARLs({ commissionId })).pipe(
                    map((result) =>
                        result.reduce(
                            (dict, arl) => {
                                dict[arl.Id] = arl;
                                return dict;
                            },
                            {} as Record<number, AccountsReceivableLedgerEntity>,
                        ),
                    ),
                );
            }),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        const getOrdersByTypes$ = (types: LedgerImportStatusEnum[]) => orders$.pipe(map((orders) => orders.filter((order) => types.includes(order.Status))));
        const materialsFromOrders$ = (o: Observable<LedgerImportEntity[]>): Observable<{ order: LedgerImportEntity; arls: AccountsReceivableLedgerEntity[] }[]> =>
            combineLatest([o, commissionArls$]).pipe(
                map(([orders, arls]) =>
                    orders.map((order) => {
                        return {
                            order,
                            arls: order.AccountsReceivableLedgerIds.map((arlId) => _getArlChildren(arlId, arls, false))
                                .flat()
                                .filter((arl) => arl.MaterialId && arl.Type === AccountsReceivableLedgerTypeEnum.MaterialArl),
                        };
                    }),
                ),
                shareReplay({ refCount: true, bufferSize: 1 }),
            );
        const orders$ = this.commissionId$.pipe(
            switchMap((commissionId) => commissionId ?
                firstValueFrom(store.select(getLedgerImportByCommissionId({ commissionId: commissionId }))).then((lis) => lis.filter((li) => li.DocumentType === documentType)) : of([] as LedgerImportEntity[])
            ),
        );
        this.data$ = combineLatest([arls$, store.select(getQuantityTypeDictionary), store.select(getCustomerDictionary)]).pipe(
            switchMap(([arls, quantityTypes, customers]) => {
                const materialArls = arls.filter((arl) => arl.Type !== AccountsReceivableLedgerTypeEnum.Text && arl.Type !== AccountsReceivableLedgerTypeEnum.Group);
                const materialIds = uniqArray(materialArls.filter((a) => a.MaterialId !== null).map((a) => a.MaterialId)).sort()
                const materialToSuppliers$ = materialIds.length ? this.materialToSupplierDataService$.getMaterialToSupplierByMaterialIds$(materialIds).pipe(shareReplay({ refCount: true, bufferSize: 1 })) : of([] as MaterialToSupplierEntity[]);
                const dataSource = materialArls
                    .reduce(
                        (p, c) => {
                            const sameMaterialAndSupplierIndex = p.findIndex((arl) => arl.MaterialId !== null && arl.MaterialId === c.MaterialId && (c.SupplierId || null) === (arl.SupplierId || null));
                            if (sameMaterialAndSupplierIndex >= 0) {
                                p[sameMaterialAndSupplierIndex].Quantity += c.Quantity;
                            } else {
                                p.push(c);
                            }
                            return p;
                        },
                        [] as {
                            Id: number;
                            MaterialId: number;
                            Type: AccountsReceivableLedgerTypeEnum;
                            Quantity: number;
                            QuantityTypeId: number;
                            BookingText: string;
                            Information: string;
                            CostAmount: number;
                            Tax: number;
                            SupplierId: number;
                        }[],
                    )
                    .map((arl) => {
                        const filterArls = (orderArls: orderMaterialArls) => {
                            return orderArls
                                .map(({ arls }) => arls)
                                .flat()
                                .filter((a) => a.MaterialId !== null &&  a.MaterialId === arl.MaterialId);
                        };
                        const currencyCode = 'EUR'; // todo richtigen currencycode holen

                        const getQuantityFromMaterials$ = (o: Observable<{ order: LedgerImportEntity; arls: AccountsReceivableLedgerEntity[] }[]>) =>
                            combineLatest([o, commissionId$.pipe(distinctUntilChanged())]).pipe(
                                map(([activeOrderMaterialArls, commissionId]) => {
                                    if (commissionId) {
                                        return filterArls(activeOrderMaterialArls.filter(({ order }) => order.CommissionId === commissionId)).reduce((p, c) => p + (c.Quantity || 0), 0);
                                    } else {
                                        return null;
                                    }
                                }),
                                shareReplay({ refCount: true, bufferSize: 1 }),
                            );

                        const alreadyOrderedQuantityForCommissionNew$ = getQuantityFromMaterials$(materialsFromOrders$(getOrdersByTypes$([LedgerImportStatusEnum.Neu])));
                        const alreadyOrderedQuantityForCommissionShipped$ = getQuantityFromMaterials$(materialsFromOrders$(getOrdersByTypes$([LedgerImportStatusEnum.Versandt])));
                        const alreadyOrderedQuantityForCommissionBooked$ = getQuantityFromMaterials$(materialsFromOrders$(getOrdersByTypes$([LedgerImportStatusEnum.Gebucht])));

                        const id = arl.Type === AccountsReceivableLedgerTypeEnum.MaterialArl ? arl.MaterialId + '|' + (arl.SupplierId || 0) : 'NM' + arl.Id;
                        if (!this.quantityFormCache.has(id)) {
                            this.quantityFormCache.set(id, new FormControl(0));
                        }
                        if (!this.supplierFormCache.has(id)) {
                            this.supplierFormCache.set(id, new FormControl(arl.SupplierId ? customers[arl.SupplierId] : null));
                        }

                        const quantityForm = this.quantityFormCache.get(id);
                        const supplierForm = this.supplierFormCache.get(id);

                        const costAmountFromMaterialToSupplierOrMaterialAsNumber$ = materialToSuppliers$.pipe(
                            switchMap((m2ss) =>
                                supplierForm.valueChanges.pipe(
                                    startWith(null),
                                    map(() => supplierForm.value),
                                    switchMap((supplier) => {
                                        const costFromSupplier = m2ss.find((m2s) => m2s.SupplierId === supplier?.Id && m2s.MaterialId === arl.MaterialId)?.Cost;
                                        if (isNotNullOrUndefined(costFromSupplier)) {
                                            return of(financialRound(costFromSupplier/100));
                                        } else {
                                            if(!isNotNullOrUndefined(arl.MaterialId)) {
                                                return of(arl.CostAmount);
                                            }
                                            return this.materialDataService$.getMaterialById$(arl.MaterialId).pipe(map((m) => isNotNullOrUndefined(m?.Cost) ? financialRound(m.Cost/100) : null));

                                        }
                                    }),
                                ),
                            ),
                        );
                        const ret: NewOrderFromLedgerimportItem = {
                            ArlId: arl.Id,
                            materialId: arl.MaterialId,
                            Type: arl.Type.toString(),
                            Quantity: arl.Quantity,
                            QuantityTypeId: arl.QuantityTypeId,
                            quantityType: arl.QuantityTypeId ? quantityTypes[arl.QuantityTypeId]?.Name : '',
                            supplierName$: supplierForm.valueChanges.pipe(startWith(null), map(() => supplierForm.value?.Name || '')),
                            bookingText$: arl.BookingText
                                ? of(arl.BookingText)
                                : arl.Type === AccountsReceivableLedgerTypeEnum.MaterialArl ? supplierForm.valueChanges.pipe(
                                      startWith(null),
                                      map(() => supplierForm.value),
                                      switchMap((supplier) =>
                                          supplier
                                              ? materialToSuppliers$.pipe(
                                                    switchMap((m2ss) => {
                                                        const articleNumberFromSupplier = m2ss.find((m2s) => m2s.SupplierId === supplier.Id && m2s.MaterialId === arl.MaterialId)?.ArticleNumber;
                                                        if (articleNumberFromSupplier) {
                                                            return of(articleNumberFromSupplier);
                                                        } else {
                                                            return this.materialDataService$.getMaterialById$(arl.MaterialId).pipe(map((m) => m?.ArticleNumber || ''));
                                                        }
                                                    }),
                                                )
                                              : arl?.MaterialId
                                                  ? this.materialDataService$.getMaterialById$(arl?.MaterialId).pipe(map((m) => m?.ArticleNumber || ''))
                                                  : of('')
                                      ),
                                  ) : of(''),
                            Information: arl.Information,
                            CostAmount: arl.CostAmount,
                            Tax: arl.Tax,
                            // supplierName: arl.SupplierId ? customers[arl.SupplierId]?.Name : '',
                            costAmount: formatCurrency(arl.CostAmount, 'de-DE', getCurrencySymbol(currencyCode, 'narrow'), currencyCode),
                            costAmountFromMaterialToSupplierOrMaterialAsNumber$,
                            costAmountFromMaterialToSupplierOrMaterial$: costAmountFromMaterialToSupplierOrMaterialAsNumber$.pipe(
                                map((cost) => (isNotNullOrUndefined(cost) ? formatCurrency(cost, 'de-DE', getCurrencySymbol(currencyCode, 'narrow'), currencyCode) : '')),
                            ),
                            // SupplierId: arl.SupplierId,
                            alreadyOrderedQuantityForCommissionNew$,
                            alreadyOrderedQuantityForCommissionShipped$,
                            alreadyOrderedQuantityForCommissionBooked$,
                            id,
                            orderedDiff$: commissionId$.pipe(
                                map((id) => !!id),
                                distinctUntilChanged(),
                                switchMap((commissionSelected) => (commissionSelected ? alreadyOrderedQuantityForCommissionNew$ : of(0))),
                                map((ordered) => ordered - arl.Quantity),
                                shareReplay({ refCount: true, bufferSize: 1 }),
                            ),
                            quantityForm,
                            supplierForm,
                        };
                        return ret;
                    });
                return supplierId$.pipe(
                    distinctUntilChanged(),
                    switchMap((supplierId) => {
                        if (supplierId && dataSource.length) {
                            this.isLoadingResults$.next(true);
                            return materialToSuppliers$.pipe(
                                map((m2s) => {
                                    this.isLoadingResults$.next(false);
                                    const matIdsBySupplier = uniqArray(m2s.filter((m2s) => m2s.SupplierId === supplierId).map((m2s) => m2s.MaterialId));
                                    return dataSource.filter((arl) => arl.supplierForm.value?.Id === supplierId || matIdsBySupplier.includes(arl.materialId));
                                }),
                            );
                        } else {
                            return of(dataSource);
                        }
                    }),
                );
            }),
            shareReplay({ refCount: true, bufferSize: 1 }),
        );
        this.length$ = this.data$.pipe(map((d) => d.length || 0));
    }

    public arlFilterValues$: BehaviorSubject<ArlFilterValues> = new BehaviorSubject({
        [FilterTypes.ArlType]: Array.from(ARLTypeEnumNames, ([val, key]) => ({
            id: val,
            label: key,
        })).filter((v) => v.id === AccountsReceivableLedgerTypeEnum.MaterialArl || v.id === AccountsReceivableLedgerTypeEnum.Other  || v.id === AccountsReceivableLedgerTypeEnum.ExternalService),
    });

    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<NewOrderFromLedgerimportItem[]> {
        if (this.paginator && this.sort) {
            // Combine everything that affects the rendered data into one update
            // stream for the data-table to consume.
            this.resetFormSubscription = this.commissionId$
                .pipe(
                    distinctUntilChanged(),
                    debounceTime(10), // dirty fix, for wait on alreadyOrderedQuantityForCommission$
                    withLatestFrom(this.data$),
                )
                .subscribe(async ([cId, data]) => {
                    if (cId) {
                        await Promise.all(
                            data.map((d) => firstValueFrom(d.alreadyOrderedQuantityForCommissionNew$).then((quantityFromCommission) => d.quantityForm.setValue(quantityFromCommission > d.Quantity ? 0 : d.Quantity - quantityFromCommission))),
                        );
                    } else {
                        data.forEach((d) => d.quantityForm.setValue(d.Quantity));
                    }
                });


            this.filteredData$ = merge(this.data$, this.arlFilterValues$).pipe(
                withLatestFrom(this.data$),
                map(([_, data]) => this.getFilteredData(data)),
                shareReplay({ refCount: true, bufferSize: 1 })
            );

            return merge(this.data$, this.paginator.page, this.sort.sortChange, this.arlFilterValues$).pipe(
                withLatestFrom(this.data$),
                map(([_, data]) => {
                    const filtered = this.getFilteredData(data);
                    this.filteredDataLength$.next(filtered.length);
                    this.paginator.length = filtered.length;
                    //this.paginator.firstPage();
                    return filtered;
                }),
                switchMap((data) =>
                    merge(this.paginator.page, this.sort.sortChange).pipe(
                        startWith(null),
                        map(() => this.getPagedData(this.getSortedData([...data]))),
                    ),
                ),
            );
        } else {
            throw Error('Please set the paginator and sort on the data source before connecting.');
        }
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect(): void {
        this.resetFormSubscription?.unsubscribe();
    }

    private getFilteredData(data: NewOrderFromLedgerimportItem[]): NewOrderFromLedgerimportItem[] {
        const filterValues = this.arlFilterValues$.value;
        if (filterValues?.[FilterTypes.ArlType]?.length) {
            return data.filter((d) => filterValues[FilterTypes.ArlType].some((s) => s.id === d.Type));
        } else {
            return data;
        }
    }

    /**
     * Paginate the data (client-side). If you're using server-side pagination,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getPagedData(data: NewOrderFromLedgerimportItem[]): NewOrderFromLedgerimportItem[] {
        if (this.paginator) {
            const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
            return data.splice(startIndex, this.paginator.pageSize);
        } else {
            return data;
        }
    }

    /**
     * Sort the data (client-side). If you're using server-side sorting,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getSortedData(data: NewOrderFromLedgerimportItem[]): NewOrderFromLedgerimportItem[] {
        if (!this.sort || !this.sort.active || this.sort.direction === '') {
            return data;
        }

        return data.sort((a, b) => {
            const isAsc = this.sort?.direction === 'asc';
            switch (this.sort?.active as keyof NewOrderFromLedgerimportItem) {
                case 'costAmount':
                    return compare(a.CostAmount, b.CostAmount, isAsc);
                default:
                    return compare(a[this.sort?.active], b[this.sort?.active], isAsc);
            }
        });
    }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean): number {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
